import { Component } from '@theme/component';

/**
 * Ax Comparateur — slider desktop.
 *
 * Chaque groupe objectif contient un carousel (`.ax-comparateur__carousel`) avec :
 *   - un `<swiper-container init="false">` vide,
 *   - un `[data-ax-fallback]` (les tuiles rendues au SSR),
 *   - une paire de flèches.
 *
 * À l'init : on déplace les tuiles du fallback dans des `<swiper-slide>`, on configure
 * Swiper (3 par vue en desktop ≥1200px, peek mobile), puis `.initialize()`. Reprend le
 * pattern de `@theme/ax-tabbed-base` mais pour N carousels indépendants (pas d'onglets).
 * Les flèches (`on:click="/previous"` / `/next`) pilotent le carousel dont elles sont issues.
 *
 * @extends {Component}
 */
class AxComparateurSlider extends Component {
  requiredRefs = [];

  /** @type {Map<HTMLElement, { swiper: any, prevBtn: HTMLButtonElement | null, nextBtn: HTMLButtonElement | null }>} */
  #swipers = new Map();

  async connectedCallback() {
    super.connectedCallback();

    const { carousels } = this.refs;
    if (!carousels) return;

    await customElements.whenDefined('swiper-container');

    const list = Array.isArray(carousels) ? carousels : [carousels];
    for (const carousel of list) {
      this.#initCarousel(carousel);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const { swiper } of this.#swipers.values()) {
      try {
        swiper?.destroy(true, true);
      } catch (_) {
        // Swiper peut throw pendant le teardown s'il est déjà détruit.
      }
    }
    this.#swipers.clear();
  }

  /** @param {Event} event */
  previous(event) {
    this.#swiperFrom(event)?.slidePrev();
  }

  /** @param {Event} event */
  next(event) {
    this.#swiperFrom(event)?.slideNext();
  }

  /**
   * @param {Event} event
   * @returns {any | undefined}
   */
  #swiperFrom(event) {
    const carousel = /** @type {HTMLElement} */ (event.target)?.closest?.('.ax-comparateur__carousel');
    return carousel ? this.#swipers.get(carousel)?.swiper : undefined;
  }

  /**
   * Wrappe les tuiles SSR en `<swiper-slide>` et initialise Swiper. Idempotent.
   * @param {HTMLElement} carousel
   */
  #initCarousel(carousel) {
    if (this.#swipers.has(carousel)) return;

    const swiperEl = /** @type {HTMLElement & { swiper: any, initialize: () => void }} */ (
      carousel.querySelector('swiper-container.ax-comparateur__swiper')
    );
    const fallback = carousel.querySelector('[data-ax-fallback]');
    if (!swiperEl || !fallback) return;

    const items = [...fallback.children];
    if (!items.length) return;

    // Réserve la hauteur pour éviter le collapse pendant le swap fallback → swiper.
    const reserved = carousel.getBoundingClientRect().height;
    if (reserved > 0) carousel.style.minHeight = `${reserved}px`;

    for (const item of items) {
      const slide = document.createElement('swiper-slide');
      slide.className = 'ax-comparateur__slide';
      while (item.firstChild) slide.appendChild(item.firstChild);
      swiperEl.appendChild(slide);
    }
    fallback.remove();

    Object.assign(swiperEl, this.#config(swiperEl));
    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    requestAnimationFrame(() => {
      carousel.style.minHeight = '';
    });

    const { swiper } = swiperEl;
    if (!swiper) return;

    const prevBtn = /** @type {HTMLButtonElement | null} */ (carousel.querySelector('.slideshow-control--previous'));
    const nextBtn = /** @type {HTMLButtonElement | null} */ (carousel.querySelector('.slideshow-control--next'));
    this.#swipers.set(carousel, { swiper, prevBtn, nextBtn });

    const syncDisabled = () => {
      if (prevBtn) prevBtn.disabled = Boolean(swiper.isBeginning);
      if (nextBtn) nextBtn.disabled = Boolean(swiper.isEnd);
    };
    syncDisabled();
    for (const evt of ['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'resize']) {
      swiper.on(evt, syncDisabled);
    }

    swiper.update();
  }

  /**
   * @param {HTMLElement} swiperEl
   * @returns {Record<string, any>}
   */
  #config(swiperEl) {
    const gap = parseInt(swiperEl.dataset.gap ?? '', 10) || 12;

    // Bleed mobile : le carousel est sorti des marges de page en CSS, on
    // réinjecte la marge en offset de slides pour que le premier reste aligné
    // sur le texte tout en laissant les cartes filer jusqu'aux bords.
    const pageMargin = parseFloat(getComputedStyle(swiperEl).getPropertyValue('--page-margin')) || 16;

    return {
      slidesPerView: 1.15,
      slidesPerGroup: 1,
      spaceBetween: gap,
      slidesOffsetBefore: pageMargin,
      slidesOffsetAfter: pageMargin,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      breakpoints: {
        1200: { slidesPerView: 3, slidesPerGroup: 1, slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
      },
    };
  }
}

if (!customElements.get('ax-comparateur-slider')) {
  customElements.define('ax-comparateur-slider', AxComparateurSlider);
}
